/**
 * @fileoverview Cart drawer: overlay, section HTML refresh after add-to-cart, optional order-note accordion.
 * Depends on `SiteOverlay`, `routes`, `fetchConfig`, `debounce`, `ON_CHANGE_DEBOUNCE_TIMER`, and pub/sub globals.
 */

/**
 * Host element for the slide-out cart; coordinates overlay, focus, and drawer section updates.
 *
 * @extends {HTMLElement}
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    if (__st.p == 'cart') return false;

    this.variantSelector = document.querySelector('variant-selects');
    this.setHeaderCartIconAccessibility();

    /** @type {(e: KeyboardEvent) => void} Closes drawer on Escape when open. */
    this.boundDocumentKeyup = (event) => {
      if (event.code.toUpperCase() === 'ESCAPE' && this.classList.contains('active')) {
        this.close();
      }
    };
    document.addEventListener('keyup', this.boundDocumentKeyup);

    this.cartNoteAccordion = document.querySelector('[data-order-note-title]');
    this.cartNoteAccordionContent = document.querySelector('[data-order-note-content]');

    this.cartNoteAccordion?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.toggleCartNoteAccordion();
    });

    /** @type {(e: MouseEvent) => void} Collapses order note when clicking outside drawer UI. */
    this.boundHandleDocumentClick = (event) => this.handleDocumentClick(event);
    document.addEventListener('click', this.boundHandleDocumentClick);
  }

  /**
   * Toggles the optional order-note panel inside the drawer.
   */
  toggleCartNoteAccordion() {
    this.cartNoteAccordionContent.classList.toggle('open');
  }

  disconnectedCallback() {
    if (this.boundDocumentKeyup) {
      document.removeEventListener('keyup', this.boundDocumentKeyup);
    }
    if (this.boundHandleDocumentClick) {
      document.removeEventListener('click', this.boundHandleDocumentClick);
    }
  }

  /**
   * Collapses the order-note accordion when the drawer is open and the user clicks outside title + content.
   * @param {MouseEvent} event
   */
  handleDocumentClick(event) {
    if (!this.classList.contains('active')) return;
    if (!this.cartNoteAccordionContent || !this.cartNoteAccordionContent.classList.contains('open')) return;

    const clickTarget = /** @type {Node} */ (event.target);
    const clickedInsideContent = this.cartNoteAccordionContent.contains(clickTarget);
    const clickedOnTitle = this.cartNoteAccordion && this.cartNoteAccordion.contains(clickTarget);

    if (!clickedInsideContent && !clickedOnTitle) {
      this.cartNoteAccordionContent.classList.remove('open');
    }
  }

  /**
   * Turns the header cart link into a drawer trigger (button semantics, Space activation).
   */
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Opens the drawer, shows overlay, traps scroll, moves focus into the drawer.
   * @param {HTMLElement} [triggeredBy] - Element to restore focus context (e.g. cart icon).
   */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    SiteOverlay.showOverlay();
    this.classList.add('active');
    Utility.forceFocus(document.querySelector('#CartDrawer'));
    document.body.classList.add('scroll-fixed');
  }

  /**
   * Closes the drawer and restores page scroll.
   */
  close() {
    this.classList.remove('active');
    SiteOverlay.hideOverlay();
    document.body.classList.remove('scroll-fixed');
  }

  /**
   * Injects section HTML from a cart/add response and syncs header count; then opens the drawer.
   * @param {object} parsedState - Shopify cart JSON with `sections` map (includes `cart-drawer`).
   */
  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (parsedState?.sections['cart-drawer'] != null) {
      const jsonNode = Utility.DOM.extract(parsedState.sections['cart-drawer'], '[data-cartJSON]');
      if (jsonNode?.innerHTML) {
        window.cartStrings.cart = JSON.parse(jsonNode.innerHTML);
        Utility.CartUi.syncHeaderFromCartLike(window.cartStrings.cart);
      }
    }

    setTimeout(() => {
      this.open();
    });
  }

  /**
   * Returns inner HTML of the first match inside a section HTML string.
   * @param {string} html - Section render HTML from the Ajax API.
   * @param {string} [selector='.shopify-section'] - Root selector inside the fragment.
   * @returns {string}
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = Utility.DOM.extract(html, selector);
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  /**
   * Parses section HTML to a document fragment root element (unused by current drawer flow; kept for parity).
   * @param {string} html
   * @param {string} [selector='.shopify-section']
   * @returns {Element|null}
   */
  getSectionDOM(html, selector = '.shopify-section') {
    return Utility.DOM.extract(html, selector);
  }

  /**
   * @param {HTMLElement} element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/**
 * Cart line list inside the drawer; re-renders the `cart-drawer` section on quantity changes.
 *
 * @extends {CartItems}
 */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    /**
     * Debounced cart note field; persists via `cart/update.js` and collapses accordion on save.
     *
     * @extends {HTMLElement}
     */
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.saveNoteBtn = this.querySelector('[data-save-note]');

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
        this.saveNoteBtn?.addEventListener('click', this.saveNote.bind(this));
      }

      /**
       * Closes the order-note accordion after explicit save (UX affordance).
       * @param {Event} event
       */
      saveNote(event) {
        event.preventDefault();
        this.closest('[data-order-note-content]').classList.remove('open');
      }
    }
  );
}

if (!customElements.get('cart-gstin')) {
  customElements.define(
    'cart-gstin',
    /**
     * Always-visible GSTIN field; normalizes to uppercase and persists as cart attribute `GSTIN`.
     *
     * @extends {HTMLElement}
     */
    class CartGstin extends HTMLElement {
      constructor() {
        super();

        this.input = this.querySelector('[data-cart-gstin-input]');
        if (!this.input) return;

        this.persistGstin = debounce(() => {
          this.#saveAttribute();
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.input.addEventListener('input', (event) => {
          const target = /** @type {HTMLInputElement} */ (event.target);
          const caret = target.selectionStart;
          const normalized = target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15);

          if (target.value !== normalized) {
            target.value = normalized;
            if (typeof caret === 'number') {
              target.setSelectionRange(caret, caret);
            }
          }

          this.persistGstin();
        });

        this.input.addEventListener('change', () => this.#saveAttribute());
      }

      /**
       * Writes `attributes.GSTIN` via Shopify Cart Ajax API.
       */
      #saveAttribute() {
        if (!this.input) return;

        const body = JSON.stringify({
          attributes: {
            GSTIN: this.input.value.trim(),
          },
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }
    }
  );
}
